import {
  ResponseError,
  type ErrorHandlerFunction,
  type HandledResponse,
  type ApiResponse,
} from '@/api/generator/runtime';
import { flowAsync } from './flowAsync';
import { usePermissionsStore } from '@/stores/permissionsStore';

export function errorFlowAsync(...errorHandler: ErrorHandlerFunction[]) {
  return flowAsync(...errorHandler);
}

/**
 * Error handling function that always resolves the passed in promise.
 * Passing in addional error handling function of type void will stop the sequential execution (default error handler will not be triggered) otherwise it should return ApiError to call next handler in line.
 * @export
 * @template T Template type will be infered from the passed in promise
 * @param {Promise<T>} promise Promise to be resolved
 * @param {...ErrorHandlerFunction[]} errorHandlers Additional error handling functions (optional)
 * @param {(reponse: Reponse) => Error} errorMapper function to get specific Error for response before handling
 * @return {*}  {Promise<HandledResponse<T>>}
 */
export async function handle<T>(
  promise: Promise<ApiResponse<T>>,
  errorHandlers: ErrorHandlerFunction[] = [],
  errorMapper: (response: Response) => Error | Promise<Error> = () => new Error('Some Api Error'),
): Promise<HandledResponse<T>> {
  try {
    const resultPromise = await promise;
    const resultValue = await resultPromise.value();

    const permissionsUpdated = resultPromise.raw.headers.get('Userrolepermissions') === 'Updated';

    if (permissionsUpdated) await usePermissionsStore().fetchMyPermissions();

    return { result: resultValue, success: true, raw: resultPromise.raw };
  } catch (reason: any) {
    // actually, only ResponseErrors should occur here (triggered by the generated API)
    const error: Error =
      reason instanceof ResponseError ? await errorMapper(reason.response) : reason;

    await errorFlowAsync(...errorHandlers)(error);

    return { result: undefined, success: false, raw: reason?.response };
  }
}
